import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, MapPin, Plus, Edit2, Trash2, X, Loader2, Check } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Grain } from "../components/Grain";

const EMPTY = { label: "", line1: "", line2: "", city: "", state: "", pincode: "", notes: "" };

const AccountPage = () => {
    const { user, loading: authLoading, refresh, requireAuth } = useAuth();
    const { lang } = useLang();
    const t = (en, te) => (lang === "en" ? en : te);

    const [editing, setEditing] = useState(null); // null | 'new' | address.id
    const [form, setForm] = useState(EMPTY);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [deletingId, setDeletingId] = useState("");

    const addresses = user?.addresses || [];

    useEffect(() => {
        if (!authLoading && !user) {
            requireAuth(() => window.location.reload(), "login");
        }
    }, [authLoading, user, requireAuth]);

    const openNew = () => { setForm(EMPTY); setErr(""); setEditing("new"); };
    const openEdit = (a) => {
        setForm({
            label: a.label || "",
            line1: a.line1 || "",
            line2: a.line2 || "",
            city: a.city || "",
            state: a.state || "",
            pincode: a.pincode || "",
            notes: a.notes || "",
        });
        setErr("");
        setEditing(a.id);
    };
    const cancel = () => { setEditing(null); setErr(""); setForm(EMPTY); };

    const validate = () => {
        if (!form.line1.trim() || !form.city.trim() || !form.state.trim() || !/^\d{6}$/.test(form.pincode)) {
            setErr(t("Please fill address, city, state and a valid 6-digit pincode.", "చిరునామా, నగరం, రాష్ట్రం, 6 అంకెల పిన్‌కోడ్ నమోదు చేయండి."));
            return false;
        }
        return true;
    };

    const save = async () => {
        setErr("");
        if (!validate()) return;
        setBusy(true);
        try {
            if (editing === "new") {
                await api.post("/auth/me/addresses", form);
            } else {
                await api.put(`/auth/me/addresses/${editing}`, form);
            }
            await refresh();
            cancel();
        } catch (e) { setErr(errMsg(e)); }
        finally { setBusy(false); }
    };

    const remove = async (id) => {
        if (!window.confirm(t("Delete this address?", "ఈ చిరునామాను తొలగించాలా?"))) return;
        setDeletingId(id);
        try {
            await api.delete(`/auth/me/addresses/${id}`);
            await refresh();
        } catch (e) { alert(errMsg(e)); }
        finally { setDeletingId(""); }
    };

    return (
        <div className="min-h-screen bg-[color:var(--kp-cream)]">
            <Grain />
            <Header />
            <main className="pt-28 md:pt-36 pb-24">
                <div className="max-w-[900px] mx-auto px-6 md:px-10">
                    <Link
                        to="/"
                        className="inline-flex items-center gap-2 text-[11px] tracking-mono-wide text-[color:var(--kp-ink)]/60 hover:text-[color:var(--kp-terracotta)] mb-6"
                        data-testid="back-home"
                    >
                        <ArrowLeft size={12} />
                        {t("Back to the shop", "షాప్‌కు తిరిగి")}
                    </Link>

                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, ease: [0.19, 1, 0.22, 1] }}
                        className={`font-display text-5xl md:text-6xl leading-[0.95] tracking-editorial mb-2 font-bold`}
                        data-testid="account-title"
                    >
                        {t("Your", "మీ")}{" "}
                        <em className="italic text-[color:var(--kp-terracotta)]">
                            {t("addresses.", "చిరునామాలు.")}
                        </em>
                    </motion.h1>
                    <p className="text-[color:var(--kp-ink-soft)] max-w-lg mb-8">
                        {t(
                            "Save home, office, parents' place — anywhere the pouches need to reach. Reuse them in one tap at checkout.",
                            "ఇల్లు, ఆఫీస్, అమ్మవాళ్ళ ఇల్లు — ఎక్కడైనా ప్యాకెట్లు పంపగలం. చెక్అవుట్‌లో ఒక్క క్లిక్‌తో ఎంచుకోండి.",
                        )}
                    </p>

                    {authLoading ? (
                        <div className="text-[color:var(--kp-ink)]/50 py-16" data-testid="account-loading">
                            {t("Loading…", "లోడ్ అవుతోంది…")}
                        </div>
                    ) : !user ? (
                        <div className="border border-[color:var(--kp-ink)]/15 p-8 text-center rounded-2xl" data-testid="account-need-auth">
                            {t("Please log in to manage your addresses.", "మీ చిరునామాలు నిర్వహించడానికి లాగిన్ అవండి.")}
                        </div>
                    ) : (
                        <>
                            <div className="space-y-3 mb-4" data-testid="addresses-list">
                                {addresses.length === 0 && !editing && (
                                    <div className="border border-dashed border-[color:var(--kp-ink)]/20 p-10 text-center rounded-2xl" data-testid="addresses-empty">
                                        <MapPin className="mx-auto mb-3 opacity-40" size={26} />
                                        <p className="font-display italic text-2xl text-[color:var(--kp-ink)]/60">
                                            {t("No addresses saved yet.", "సేవ్ చేసిన చిరునామాలు లేవు.")}
                                        </p>
                                        <p className="text-sm text-[color:var(--kp-ink-soft)] mt-2">
                                            {t("Add one below, or save it at checkout.", "కింద చేర్చండి లేదా చెక్అవుట్‌లో సేవ్ చేయండి.")}
                                        </p>
                                    </div>
                                )}

                                {addresses.map((a) => (
                                    <div
                                        key={a.id}
                                        className="border-2 border-[color:var(--kp-ink)]/10 bg-[color:var(--kp-paper)] rounded-2xl p-5"
                                        data-testid={`address-card-${a.id}`}
                                    >
                                        <div className="flex justify-between items-start gap-4">
                                            <div className="min-w-0 flex-1">
                                                {a.label && (
                                                    <div className="text-[10px] tracking-mono-wide text-[color:var(--kp-terracotta)] font-bold mb-1">
                                                        {a.label.toUpperCase()}
                                                    </div>
                                                )}
                                                <div className="text-sm text-[color:var(--kp-ink)] leading-snug">
                                                    {a.line1}{a.line2 ? `, ${a.line2}` : ""}
                                                </div>
                                                <div className="text-xs text-[color:var(--kp-ink-soft)] mt-0.5">
                                                    {a.city}, {a.state} — {a.pincode}
                                                </div>
                                                {a.notes && (
                                                    <div className="text-xs text-[color:var(--kp-ink-soft)] italic mt-1">
                                                        {a.notes}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-2 shrink-0">
                                                <button
                                                    onClick={() => openEdit(a)}
                                                    className="p-2 rounded-full border border-[color:var(--kp-ink)]/15 hover:border-[color:var(--kp-terracotta)] hover:text-[color:var(--kp-terracotta)] transition-colors"
                                                    aria-label="Edit"
                                                    data-testid={`edit-address-${a.id}`}
                                                >
                                                    <Edit2 size={13} />
                                                </button>
                                                <button
                                                    onClick={() => remove(a.id)}
                                                    disabled={deletingId === a.id}
                                                    className="p-2 rounded-full border border-[color:var(--kp-ink)]/15 hover:border-[color:var(--kp-terracotta)] hover:text-[color:var(--kp-terracotta)] transition-colors disabled:opacity-50"
                                                    aria-label="Delete"
                                                    data-testid={`delete-address-${a.id}`}
                                                >
                                                    {deletingId === a.id ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {!editing && (
                                <button
                                    onClick={openNew}
                                    className="w-full flex items-center justify-center gap-2 rounded-full py-3.5 border-2 border-dashed border-[color:var(--kp-terracotta)]/40 text-[color:var(--kp-terracotta)] text-[12px] tracking-mono-wide font-bold hover:bg-[color:var(--kp-terracotta)]/5 transition-colors"
                                    data-testid="add-address-button"
                                >
                                    <Plus size={14} /> {t("ADD NEW ADDRESS", "కొత్త చిరునామా చేర్చండి")}
                                </button>
                            )}

                            <AnimatePresence>
                                {editing && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: 20 }}
                                        className="mt-6 border-2 border-[color:var(--kp-terracotta)]/30 rounded-2xl p-5 md:p-6 bg-[color:var(--kp-paper)]"
                                        data-testid="address-editor"
                                    >
                                        <div className="flex items-center justify-between mb-4">
                                            <div className="text-[10px] tracking-mono-wide text-[color:var(--kp-terracotta)] font-bold">
                                                {editing === "new"
                                                    ? t("NEW ADDRESS", "కొత్త చిరునామా")
                                                    : t("EDIT ADDRESS", "చిరునామా మార్చండి")}
                                            </div>
                                            <button onClick={cancel} aria-label="Cancel" className="p-1" data-testid="editor-cancel">
                                                <X size={16} />
                                            </button>
                                        </div>

                                        {err && (
                                            <div className="mb-3 text-sm text-[color:var(--kp-terracotta)] bg-[color:var(--kp-terracotta)]/8 border border-[color:var(--kp-terracotta)]/25 px-3 py-2 rounded-xl" data-testid="editor-error">
                                                {err}
                                            </div>
                                        )}

                                        <div className="space-y-3">
                                            <Field label={t("Label (Home / Office · optional)", "లేబుల్ (ఇల్లు / ఆఫీస్ · ఐచ్ఛికం)")} value={form.label} onChange={(v) => setForm({ ...form, label: v })} testId="editor-label" />
                                            <Field label={t("Address line 1", "చిరునామా 1")} value={form.line1} onChange={(v) => setForm({ ...form, line1: v })} testId="editor-line1" />
                                            <Field label={t("Address line 2 (optional)", "చిరునామా 2 (ఐచ్ఛికం)")} value={form.line2} onChange={(v) => setForm({ ...form, line2: v })} testId="editor-line2" />
                                            <div className="grid grid-cols-2 gap-3">
                                                <Field label={t("City", "నగరం")} value={form.city} onChange={(v) => setForm({ ...form, city: v })} testId="editor-city" />
                                                <Field label={t("State", "రాష్ట్రం")} value={form.state} onChange={(v) => setForm({ ...form, state: v })} testId="editor-state" />
                                            </div>
                                            <Field label={t("Pincode", "పిన్‌కోడ్")} inputMode="numeric" value={form.pincode} onChange={(v) => setForm({ ...form, pincode: v.replace(/\D/g, "").slice(0, 6) })} testId="editor-pincode" />
                                            <Field label={t("Delivery notes (optional)", "డెలివరీ నోట్స్")} value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} testId="editor-notes" />
                                        </div>

                                        <div className="flex items-center gap-3 mt-5">
                                            <button
                                                onClick={save}
                                                disabled={busy}
                                                className="flex-1 flex items-center justify-center gap-2 rounded-full py-3 bg-[color:var(--kp-ink)] text-[color:var(--kp-cream)] text-[12px] tracking-mono-wide font-bold hover:bg-black disabled:opacity-60"
                                                data-testid="editor-save"
                                            >
                                                {busy ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
                                                {t("SAVE ADDRESS", "సేవ్ చేయండి")}
                                            </button>
                                            <button
                                                onClick={cancel}
                                                disabled={busy}
                                                className="px-5 rounded-full py-3 text-[12px] tracking-mono-wide font-bold border-2 border-[color:var(--kp-ink)]/15 text-[color:var(--kp-ink-soft)] hover:border-[color:var(--kp-ink)]/40"
                                                data-testid="editor-cancel-btn"
                                            >
                                                {t("CANCEL", "రద్దు")}
                                            </button>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </>
                    )}
                </div>
            </main>
            <Footer />
        </div>
    );
};

const Field = ({ label, value, onChange, testId, inputMode }) => (
    <label className="block">
        <span className="text-[11px] tracking-mono-wide text-[color:var(--kp-ink-soft)] mb-1 block">{label}</span>
        <input
            value={value}
            inputMode={inputMode}
            onChange={(e) => onChange(e.target.value)}
            data-testid={testId}
            className="w-full bg-[color:var(--kp-cream)] py-3 px-4 border border-[color:var(--kp-ink)]/15 focus:border-[color:var(--kp-terracotta)] rounded-full outline-none text-[color:var(--kp-ink)] transition-colors"
        />
    </label>
);

export default AccountPage;
